import { useLanguage } from '../utils/LanguageContext';
import { FormButton } from './common/ValidatedForm';
import { useAuth } from '../utils/AuthContext.js';
import translations from './ComponentsTranslations.lang.json';
import { Alert, Button, Card, Space, Table } from 'antd';
import EmployeeForm from './EmployeeForm.js';
import { EditableValues, ItemView, OneTimeActionButton } from './common/CommonComponents.js';
import EmployeeView from './EmployeeView.js';
import { EmploymentSetupStatus } from '../shared/constants/employmentConstants.js';
import { useRefreshingAsync } from './common/commonHooks.js';
import { formatName } from './common/util.js';

const {
  REQUESTED_BY_EMPLOYER,
  INITIATED_BY_EMPLOYEE,
  NAME_REVIEW_REQUIRED,
  PAYROLL_UPDATE_REQUIRED,
  ACTIVE_EMPLOYMENT,
} = EmploymentSetupStatus;

const EmployeesTable = ({ employerId, employees }) => {
  const { lang } = useLanguage(translations);
  const { api } = useAuth();

  const fullName = (employee) => formatName(employee.name);
  const statusSort = (employee) =>
    ({
      [NAME_REVIEW_REQUIRED]: 0,
      [PAYROLL_UPDATE_REQUIRED]: 1,
      [REQUESTED_BY_EMPLOYER]: 2,
      [INITIATED_BY_EMPLOYEE]: 3,
      [ACTIVE_EMPLOYMENT]: 4,
    }[employee.status]);

  const employeesSorted = employees.toSorted((a, b) =>
    a.status === b.status ? fullName(a).localeCompare(fullName(b)) : statusSort(a) - statusSort(b),
  );

  const statusHeaderColumn = (status) =>
    [NAME_REVIEW_REQUIRED, PAYROLL_UPDATE_REQUIRED].includes(status) ? (
      <Alert type="warning" message={lang(`employmentStatus.${status}`)} showIcon />
    ) : (
      lang(`employmentStatus.${status}`)
    );

  const columns = [
    { title: lang('name'), render: fullName, key: 'name' },
    { title: lang('status'), render: (record) => statusHeaderColumn(record.status), key: 'address' },
    {
      title: lang('action'),
      render: () => <Button type="link">{lang('delete')}</Button>, //TODO delete button doesn't function yet. remove or build functionality
    },
  ];

  const NameReviewDisplay = ({ title, name }) => (
    <ItemView
      title={title}
      items={[
        { field: 'givenName', value: name.givenName },
        { field: 'middleName', value: name.middleName },
        { field: 'familyName', value: name.familyName },
      ]}
    />
  );

  const renderEmployeeDetails = (employee) => (
    <Space direction="vertical">
      {employee.status === REQUESTED_BY_EMPLOYER && (
        <Alert
          message={lang('employeeRequestedTitle')}
          description={
            <Space direction="horizontal">
              {lang('employeeRequestedDescription')}
              <OneTimeActionButton
                onClick={async () => await api.resendCodeForEmployee(employerId, employee.employmentId)}
                confirmation={lang('resendCodeSucceeded')}
              >
                {lang('resendCode')}
              </OneTimeActionButton>
            </Space>
          }
          type="info"
        ></Alert>
      )}
      {employee.status === INITIATED_BY_EMPLOYEE && (
        <Alert
          message={lang('employeeInitiatedTitle')}
          description={lang('employeeInitiatedDescription')}
          type="info"
        />
      )}
      {employee.status === NAME_REVIEW_REQUIRED && (
        <Alert
          message={lang('employeeNameReviewTitle')}
          description={
            <Space direction="vertical">
              {lang('employeeNameReviewDescription')}
              <Space direction="horizontal">
                <NameReviewDisplay title={lang('userProvidedNameLabel')} name={employee.nameToReview} />
                <NameReviewDisplay title={lang('adminProvidedNameLabel')} name={employee.name} />
              </Space>
              <OneTimeActionButton
                onClick={async () => await api.confirmNameForEmployee(employerId, employee.employmentId)}
                confirmation={lang('confirmEmployeeNameSucceeded')}
              >
                {lang('confirmEmployeeName')}
              </OneTimeActionButton>
            </Space>
          }
          type="warning"
        ></Alert>
      )}
      {employee.status === PAYROLL_UPDATE_REQUIRED && (
        <Alert
          message={lang('employeePayrollUpdateTitle')}
          description={
            <Space direction="vertical">
              {lang('employeePayrollUpdateDescription')}
              <OneTimeActionButton
                onClick={async () =>
                  await api.confirmPayrollElectionEmployee(employerId, employee.employmentId, {
                    employerConfirmedPayrollElection: employee.employeeRequestedPayrollElection,
                  })
                }
                confirmation={lang('confirmEmployeePayrollElectionSucceeded')}
              >
                {lang('confirmEmployeePayrollElection')}
              </OneTimeActionButton>
            </Space>
          }
          type="warning"
        ></Alert>
      )}
      <EditableValues
        componentData={{ employerId, employee }}
        editButtonLabel={employee.status === REQUESTED_BY_EMPLOYER ? lang('editEmployee') : lang('editEmployeeId')}
        viewComponent={EmployeeView}
        editComponent={EmployeeForm}
      />
    </Space>
  );

  return (
    <Table
      columns={columns}
      expandable={{ expandedRowRender: renderEmployeeDetails }}
      dataSource={employeesSorted}
      rowKey="employmentId"
    />
  );
};

const EmployeesManager = ({ employerId, addingEmployee }) => {
  const { lang } = useLanguage(translations);
  const { api, administratedEmployers } = useAuth();

  const employer = employerId && administratedEmployers.find((e) => e.employerId === employerId);
  const employees = useRefreshingAsync(async () => await api.getEmployeesForEmployer(employerId));

  return (
    employer &&
    employees && (
      <>
        {!addingEmployee.value && (
          <Card
            title={
              <>
                {employer.employerName}: {lang('employeesSuffix')}
              </>
            }
          >
            {employees.length > 0 && <EmployeesTable employerId={employerId} employees={employees} />}
            <FormButton onClick={addingEmployee.setTrue}>{lang('addEmployee')}</FormButton>
          </Card>
        )}
        {addingEmployee.value && (
          <Card
            title={
              <>
                {employer.employerName}: {lang('newEmployee')}
              </>
            }
          >
            <EmployeeForm
              employerId={employerId}
              submitLabel={lang('addEmployee')}
              onFinish={addingEmployee.setFalse}
            />
            <FormButton onClick={addingEmployee.setFalse}>{lang('cancel')}</FormButton>
          </Card>
        )}
      </>
    )
  );
};

export { EmployeesTable };
export default EmployeesManager;
